import { useTranslation } from 'react-i18next';
import { BUSINESS_WHATSAPP } from '@/lib/reservationWhatsApp';
import '@/styles/notice.css';

/** What stopped working — picks the wording of the notice and of the WhatsApp message. */
export type NoticeSubject = 'payment' | 'booking' | 'seats' | 'tour' | 'page' | 'generic';

interface ServiceNoticeProps {
  subject?: NoticeSubject;
  /** inline: inside a form/dialog · card: in a page section · page: full-page fallback */
  variant?: 'inline' | 'card' | 'page';
  /** Ready-made WhatsApp link (e.g. with the full reservation); otherwise a polite default message is used. */
  whatsappHref?: string;
  /** Extra lines appended to the default WhatsApp message (tour name, date…), in Portuguese for the team. */
  details?: string[];
  onRetry?: () => void;
  className?: string;
}

// Message received by the Brazilian team, so it stays in Portuguese whatever the site language is.
const TEAM_SUBJECT: Record<NoticeSubject, string> = {
  payment: 'o pagamento online',
  booking: 'a reserva online',
  seats: 'a reserva online deste passeio',
  tour: 'a página do passeio',
  page: 'esta página',
  generic: 'um serviço do site',
};

function defaultWhatsAppHref(subject: NoticeSubject, details: string[]) {
  const lines = [
    `Olá! Estou no site AQUAMARINA TOURS e ${TEAM_SUBJECT[subject]} não está funcionando no momento.`,
    'Podem me ajudar a confirmar minha reserva?',
    ...details.filter(Boolean),
    typeof window !== 'undefined' ? `Página: ${window.location.href}` : '',
  ].filter(Boolean);
  return `https://wa.me/${BUSINESS_WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
}

/**
 * Respectful, professional fallback shown whenever something is unavailable (payment service down,
 * page or tour that failed to load, no seats online…). Always offers WhatsApp so the visitor can
 * still confirm the booking with the team.
 */
export default function ServiceNotice({
  subject = 'generic',
  variant = 'card',
  whatsappHref,
  details = [],
  onRetry,
  className = '',
}: ServiceNoticeProps) {
  const { t } = useTranslation();
  const href = whatsappHref || defaultWhatsAppHref(subject, details);
  const Heading = variant === 'page' ? 'h1' : 'h3';

  return (
    <div role={variant === 'page' ? undefined : 'status'} aria-live="polite" className={`service-notice service-notice--${variant} ${className}`}>
      <span className="service-notice__icon" aria-hidden="true">
        <i className="ri-customer-service-2-line" />
      </span>
      <div className="service-notice__body">
        <Heading className="service-notice__title">{t('notice.title')}</Heading>
        <p className="service-notice__text">
          {t('notice.message', { what: t(`notice.what.${subject}`) })}
        </p>
        <div className="service-notice__actions">
          <a className="service-notice__wa" href={href} target="_blank" rel="noopener noreferrer">
            <i className="ri-whatsapp-line" aria-hidden="true" />
            {t('notice.whatsapp')}
          </a>
          {onRetry && (
            <button type="button" className="service-notice__retry" onClick={onRetry}>
              <i className="ri-refresh-line" aria-hidden="true" />
              {t('notice.retry')}
            </button>
          )}
        </div>
        <p className="service-notice__hint">{t('notice.hint')}</p>
      </div>
    </div>
  );
}
